import React, { useState, useRef, useCallback } from 'react'
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
} from '@mui/material'
import {
  Close as CloseIcon,
} from '@mui/icons-material'
import 'react-image-crop/dist/ReactCrop.css'

export interface ImageCropperProps {
  /** Whether the cropper dialog is open */
  open: boolean
  /** The image file to crop */
  imageFile: File | null
  /** Callback when cropping is complete */
  onCropComplete: (croppedFile: File) => void
  /** Callback when dialog is closed/cancelled */
  onClose: () => void
  /** Aspect ratio for the crop (e.g., 1 for square, 3 for 3:1) */
  aspectRatio?: number
  /** Title for the dialog */
  title?: string
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

export const ImageCropper = React.memo(function ImageCropper({
  open,
  imageFile,
  onCropComplete,
  onClose,
  aspectRatio = 1,
  title = 'Crop Image',
}: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [imgSrc, setImgSrc] = useState('')
  const imgRef = useRef<HTMLImageElement>(null)

  // Load image when file changes
  React.useEffect(() => {
    if (imageFile) {
      const reader = new FileReader()
      reader.onload = () => {
        setImgSrc(reader.result as string)
      }
      reader.readAsDataURL(imageFile)
    } else {
      setImgSrc('')
      setCrop(undefined)
      setCompletedCrop(undefined)
    }
  }, [imageFile])

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget
      setCrop(centerAspectCrop(width, height, aspectRatio))
    },
    [aspectRatio]
  )

  const getCroppedImg = useCallback(async (): Promise<File | null> => {
    const image = imgRef.current
    if (!image || !completedCrop || !imageFile) {
      return null
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return null
    }

    // Calculate the actual crop dimensions based on the natural image size
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const cropX = completedCrop.x * scaleX
    const cropY = completedCrop.y * scaleY
    const cropWidth = completedCrop.width * scaleX
    const cropHeight = completedCrop.height * scaleY

    // Set canvas size to the cropped dimensions
    canvas.width = cropWidth
    canvas.height = cropHeight

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    )

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null)
            return
          }
          // Create a new file with the same name but cropped
          const croppedFile = new File(
            [blob],
            imageFile.name,
            { type: imageFile.type || 'image/jpeg' }
          )
          resolve(croppedFile)
        },
        imageFile.type || 'image/jpeg',
        0.95
      )
    })
  }, [completedCrop, imageFile])

  const handleCrop = async () => {
    const croppedFile = await getCroppedImg()
    if (croppedFile) {
      onCropComplete(croppedFile)
    }
  }

  const handleClose = () => {
    setImgSrc('')
    setCrop(undefined)
    setCompletedCrop(undefined)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {imgSrc && (
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                maxHeight: '60vh',
                overflow: 'auto',
                bgcolor: 'grey.100',
                borderRadius: 1,
                p: 2,
              }}
            >
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspectRatio}
                minWidth={50}
                minHeight={50}
              >
                <img
                  ref={imgRef}
                  alt="Crop preview"
                  src={imgSrc}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '55vh',
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </Box>
            <Typography variant="caption" color="text.secondary" align="center">
              Drag the corners to resize the crop area, or drag inside to reposition
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCrop}
          variant="contained"
          disabled={!completedCrop}
        >
          Crop & Use
        </Button>
      </DialogActions>
    </Dialog>
  )
})
